import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { ComponentChanges } from '@bcl/common-ui';
import { RequiredClaimsService } from '@ews/users-management';
import {
  BehaviorSubject,
  combineLatest,
  ReplaySubject,
  takeUntil,
  Subject,
} from 'rxjs';
import {
  DateRange,
  Note,
  NotesList,
  NotesListFilter,
  NotesListPageChanged,
  NotesPermissions,
  ParentContext,
} from './notes.model';
import { NotesService } from './notes.service';
import { Claims } from 'src/app/authorization';
import {
  FormModelControl,
  SelectOption,
  SnackbarService,
} from '@bcl/design-system';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NoteDialogComponent } from './note-dialog/note-dialog.component';
import { AccordionService } from 'src/app/services/accordion.service';
import { OnDestroy } from '@angular/core';

@Component({
  selector: 'app-notes-list',
  templateUrl: './notes-list.component.html',
  styleUrls: ['./notes-list.component.scss'],
})
export class NotesListComponent implements OnChanges, OnInit, OnDestroy {
  @Input()
  public titleLabel?: string;
  @Input()
  public notesList: NotesList = { notes: [], totalEntries: 0 };
  @Input()
  public canAdd = false;
  @Input()
  public canEdit = false;
  @Input()
  public canDelete = false;
  @Input()
  public pageSize = 5;
  @Input()
  public parentContext!: ParentContext;
  @Input()
  public dateFilterOptions: SelectOption[] = [];
  @Input()
  public showItemList = true;
  @Input()
  public visit?: any; // Add visit input
  @Input()
  public isEditingDisabled = false;
  @Input()
  public openByDefault = false;

  @Output()
  public loadMore = new EventEmitter<NotesListPageChanged>();
  @Output()
  public toggleVisibility = new EventEmitter<void>();

  public readonly notes$ = new BehaviorSubject<Note[]>([]);

  public permissions$ = new ReplaySubject<NotesPermissions>(1);

  public readonly filterForm: FormGroup<FormModelControl<NotesListFilter>>;

  private currentPage = 1;
  private lastPage = 0;
  private destroyed$ = new Subject<void>();

  private defaultDateFilter: DateRange | null = '4';

  constructor(
    public dialog: MatDialog,
    requiredClaimsService: RequiredClaimsService,
    private notesService: NotesService,
    fb: FormBuilder,
    public snackbarService: SnackbarService,
    private accordionService: AccordionService
  ) {
    this.filterForm = fb.group({
      date: new FormControl<DateRange | null>(this.defaultDateFilter),
    });

    this.filterForm.valueChanges.subscribe((x) => {
      this.currentPage = 1;
      this.loadMore.next({
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        filter: { ...x },
      });
    });
    combineLatest([
      requiredClaimsService.hasAccess(Claims.CreateNote),
      requiredClaimsService.hasAccess(Claims.UpdateNote),
      requiredClaimsService.hasAccess(Claims.DeleteNote),
    ]).subscribe(([canAdd, canEdit, canDelete]) => {
      this.canAdd = canAdd;
      this.canEdit = canEdit;
      this.canDelete = canDelete;
    });
  }

  // Computed property to check if form should be disabled
  get isFormDisabled(): boolean {
    return this.isEditingDisabled;
  }
  public get hasNextPage(): boolean {
    return this.currentPage < this.lastPage;
  }
  public ngOnInit(): void {
    this.loadMore.emit({
      pageNumber: 1,
      pageSize: this.pageSize,
      filter: {
        date: '4',
      },
    });

    // Open the accordion by default if specified
    if (this.openByDefault) {
      this.accordionService.openComponent('notes');
    }

    // Subscribe to accordion service to manage showItemList state
    this.accordionService
      .getCurrentOpenComponent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((openComponentId) => {
        this.showItemList = openComponentId === 'notes';
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  public ngOnChanges(changes: ComponentChanges<NotesListComponent>): void {
    if (changes.parentContext?.currentValue) {
      this.notesService.setParentContext(changes.parentContext.currentValue);
    }
    const notesList = changes.notesList?.currentValue;
    if (notesList) {
      this.notes$.next([
        ...(this.currentPage > 1 ? this.notes$.value : []),
        ...notesList.notes,
      ]);
      this.lastPage = Math.ceil(notesList.totalEntries / this.pageSize);
    }
  }
  showItems() {
    // Use accordion service to manage state
    if (this.showItemList) {
      this.accordionService.closeComponent('notes');
    } else {
      this.accordionService.openComponent('notes');
    }
  }
  public showNoteDialog(type: string, note: Note | null): void {
    const dialogRef = this.dialog.open(NoteDialogComponent, {
      disableClose: true,
      data: { note, action: type },
    });

    dialogRef
      .afterClosed()
      .subscribe(
        (
          result: { action: 'ADD' | 'UPDATE' | 'DELETE'; note: Note } | false
        ) => {
          if (result) {
            // Reset current page to 1 and clear existing notes
            this.currentPage = 1;
            this.notes$.next([]);

            switch (result.action) {
              case 'ADD':
                this.notesService
                  .addNote({
                    ...result.note,
                  })
                  .subscribe(() => {
                    this.loadMore.emit({
                      pageNumber: 1,
                      pageSize: this.pageSize,
                      filter: { ...this.filterForm.value },
                    });
                    this.snackbarService.open(
                      'green',
                      'NOTES.LIST.MESSAGES.NOTE_ADDED_SUCCESSFULLY'
                    );
                  });
                break;
              case 'UPDATE':
                this.notesService.updateNote(result.note).subscribe(() => {
                  this.loadMore.emit({
                    pageNumber: 1,
                    pageSize: this.pageSize,
                    filter: { ...this.filterForm.value },
                  });
                  this.snackbarService.open(
                    'green',
                    'NOTES.LIST.MESSAGES.NOTE_UPDATED_SUCCESSFULLY'
                  );
                });
                break;
              case 'DELETE':
                this.notesService
                  .deleteNote({
                    id: note.id,
                  })
                  .subscribe(() => {
                    this.loadMore.emit({
                      pageNumber: 1,
                      pageSize: this.pageSize,
                      filter: { ...this.filterForm.value },
                    });
                    this.snackbarService.open(
                      'green',
                      'NOTES.LIST.MESSAGES.NOTE_DELETED_SUCCESSFULLY'
                    );
                  });
                break;
            }
          }
        }
      );
  }

  public loadMoreNotes(): void {
    this.currentPage++;
    this.loadMore.next({
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      filter: { ...this.filterForm.value },
    });
  }
}
